import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { DeleteOutlined, FormOutlined, RollbackOutlined } from "@ant-design/icons";

// Internal Imports
import MarginInput from "../../../common/components/MarginInput/MarginInput";
import Paint from "../../../common/components/Paint/Paint";
import SelectBox from "../../../common/components/SelectBox/SelectBox";
import { COLORS } from "../../../common/constants/colors";
import GroupRow from "../../../common/components/GroupRow/GroupRow";
import { Mode } from "../../../common/types/AllTypes";
import { useForm } from "../../../contexts/form";
import globalStyle from "../../../common/styles/globalStyle.module.css";
import EditTextForm from "../../../common/components/EditTextForm/EditTextForm";
import IColumn from "../../../interfaces/IColumn";
import { IUsuarioProyectoRol } from "../../../interfaces/IUser";
import ButtonGrid from "../../../common/components/ButtonGrid/ButtonGrid";
import useListRol from "../../../hooks/useListRol";
import OutLineButton from "../../../common/components/OutLineButton/OutLineButton";
import DetailTable from "../../../common/components/DetailTable/DetailTable";

interface Props {
    setCanGoNext: React.Dispatch<React.SetStateAction<boolean>>;
    setCanGoBack: React.Dispatch<React.SetStateAction<boolean>>;
    mode: Mode;
}

const StepFour = ({ setCanGoNext, setCanGoBack, mode }: Props) => {
    // States
    const { listRol, loadingRols } = useListRol();
    const { setUserInformationForm, userInformationForm, } = useForm();
    const [detailUpdate, setDetailUpdate] = useState<IUsuarioProyectoRol>();
    const [listUsuarioProyectoRol, setListUsuarioProyectoRol] = useState<IUsuarioProyectoRol[]>([]);

    // Const
    const columns: IColumn[] = [
        {
            title: 'Rol',
            dataIndex: 'rolNombre',
        },
        {
            title: 'Desde',
            dataIndex: 'fechaDesde',
        },
        {
            title: 'Hasta',
            dataIndex: 'fechaHasta',
        },

        {
            title: "",
            dataIndex: "",
            render: (detail: IUsuarioProyectoRol) => (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ButtonGrid color={COLORS.black_text_color4} active={detailUpdate?.rolID !== detail.rolID && !!detailUpdate ? false : true} icon={detailUpdate?.rolID === detail.rolID ? <RollbackOutlined /> : <FormOutlined />} method={() => selectDetail(detail)} />
                    <nav>&nbsp;&nbsp;</nav>
                    <ButtonGrid color={COLORS.black_text_color4} active={!detailUpdate} icon={<DeleteOutlined />} method={() => removeDetail(detail.rolID)} />
                </div>
            )
        }
    ];

    // Methods
    const validations = (values: any) => {
        const errors: any = {};

        if (!values.rolID) {
            errors.rolID = "Requerido";
        }

        if (!values.temporal) {
            errors.temporal = "Requerido";
        }

        if (!values.fechaDesde) {
            errors.fechaDesde = "Requerido";
        }

        if (values.temporal == "true") {
            if (!values.fechaHasta) {
                errors.fechaHasta = "Requerido";
            } else if (new Date(values.fechaHasta)?.getTime() < new Date(values.fechaDesde)?.getTime()) {
                errors.fechaHasta = "No puede ser menor que Fecha Desde";
            }
        }

        return errors;
    }

    const clearForm = () => {
        UserForm.setValues({
            rolID: "",
            temporal: "false",
            fechaDesde: "",
            fechaHasta: ""
        })
    }
    const addDetail = () => {

        const newRol: IUsuarioProyectoRol = {
            usuarioProyectoRolID: 0,
            usuarioProyectoID: userInformationForm!.usuarioProyecto!.usuarioProyectoID,
            rolID: parseInt(UserForm.values.rolID),
            rolNombre: listRol.find(rol => rol.rolID === parseInt(UserForm.values.rolID))!.nombre,
            fechaDesde: UserForm.values.fechaDesde || null,
            fechaHasta: UserForm.values.fechaHasta || null
        }

        let listInternal = userInformationForm!.usuarioProyecto!.usuarioProyectoRol;
        listInternal.push(newRol);

        let userInternal = userInformationForm;
        userInternal!.usuarioProyecto!.usuarioProyectoRol = listInternal;

        setUserInformationForm({ ...userInternal! });
        clearForm();
    }

    const selectDetail = (rol: IUsuarioProyectoRol) => {
        if (!detailUpdate) {
            setDetailUpdate(rol);
            UserForm.setValues({
                rolID: rol.rolID.toString(),
                temporal: !!rol.fechaHasta ? "true" : "false",
                fechaDesde: rol.fechaDesde!,
                fechaHasta: !!rol.fechaHasta ? rol.fechaHasta : ""
            })
        } else {
            setDetailUpdate(undefined);
            clearForm();
        }
    }

    const updateDetail = () => {

        let internal: IUsuarioProyectoRol = {
            usuarioProyectoRolID: detailUpdate!.usuarioProyectoRolID,
            usuarioProyectoID: detailUpdate!.usuarioProyectoID,
            rolID: parseInt(UserForm.values.rolID),
            rolNombre: listRol.find(rol => rol.rolID === parseInt(UserForm.values.rolID))!.nombre,
            fechaDesde: UserForm.values.fechaDesde || null,
            fechaHasta: UserForm.values.fechaHasta || null
        };

        let list = userInformationForm!.usuarioProyecto?.usuarioProyectoRol!;
        let index = list.findIndex(x => x.rolID === detailUpdate!.rolID);

        list[index] = internal!;

        setUserInformationForm({
            ...userInformationForm!,
            usuarioProyecto: {
                ...userInformationForm!.usuarioProyecto!,
                usuarioProyectoRol: list
            }
        });

        // Finally
        setDetailUpdate(undefined);
        clearForm();
    }

    const removeDetail = (rolID: number) => {
        const listInternal = userInformationForm!.usuarioProyecto!.usuarioProyectoRol.filter(roll => roll.rolID !== rolID);
        let userInternal = userInformationForm;
        userInternal!.usuarioProyecto!.usuarioProyectoRol = listInternal;

        setUserInformationForm({ ...userInternal! });
    }

    // Form
    const UserForm = useFormik({
        initialValues: { rolID: "", temporal: "", fechaDesde: "", fechaHasta: "" },
        validate: validations,
        onSubmit: (values: any) => !!detailUpdate ? updateDetail() : addDetail(),
    });

    // Effects
    useEffect(() => {
        setListUsuarioProyectoRol(!!userInformationForm?.usuarioProyecto?.usuarioProyectoRol ? [...userInformationForm!.usuarioProyecto!.usuarioProyectoRol] : []);
    }, [userInformationForm])

    useEffect(() => {
        if (listUsuarioProyectoRol.length > 0 && !detailUpdate) {
            setCanGoNext(true);
        } else {
            setCanGoNext(false);
        }

        if (!!detailUpdate) {
            setCanGoBack(false);
        } else {
            setCanGoBack(true);
        }

    }, [listUsuarioProyectoRol, detailUpdate])


    return (
        <div style={{ marginTop: 20 }}>
            <GroupRow>
                <MarginInput width="100%">
                    <label>Rol:</label>
                    <SelectBox
                        value={UserForm.values.rolID}
                        placeholder={"Rol"}
                        disabled={false}
                        allowClear={false}
                        loading={loadingRols}
                        onChange={(value: string) => UserForm.setFieldValue("rolID", value)}
                        options={listRol.filter(rol => rol.departamentoID === userInformationForm?.usuarioProyecto?.departamentoID && (!userInformationForm.usuarioProyecto?.usuarioProyectoRol.map(x => x.rolID).includes(rol.rolID) || rol.rolID === detailUpdate?.rolID)).map(rol => ({ key: rol.nombre, value: rol.rolID.toString(), text: rol.nombre }))}
                        backgroundColor={COLORS.white_color2(1)}
                        borderColor={COLORS.black_text_color3}
                        textColor={COLORS.black_text_color1}
                    />
                    <p className={globalStyle.error}>
                        <Paint color={COLORS.red_error2}>{UserForm.errors.rolID && UserForm.errors.rolID}</Paint>
                    </p>
                </MarginInput>
            </GroupRow>

            <GroupRow>
                <MarginInput width="30%">
                    <label>Temporal</label>
                    <SelectBox
                        value={UserForm.values.temporal}
                        placeholder={"¿Será temporal?"}
                        disabled={false}
                        allowClear={false}
                        loading={false}
                        onChange={(value: string) => { UserForm.setValues({ ...UserForm.values, temporal: value, fechaHasta: value == 'false' ? "" : UserForm.values.fechaHasta }) }}
                        options={[{ key: 'tempSi', value: 'true', text: 'Si' }, { key: 'tempNo', value: 'false', text: 'No' }]}
                        backgroundColor={COLORS.white_color2(1)}
                        borderColor={COLORS.black_text_color3}
                        textColor={COLORS.black_text_color1}
                    />
                    <p className={globalStyle.error}>
                        <Paint color={COLORS.red_error2}>{UserForm.errors.temporal && UserForm.errors.temporal}</Paint>
                    </p>
                </MarginInput>

                <MarginInput width="30%">
                    <label>Desde:</label>
                    <EditTextForm
                        value={UserForm.values.fechaDesde?.split('T')[0]}
                        onChange={UserForm.handleChange}
                        backgroundcolor={COLORS.white_color2(1)}
                        name={"fechaDesde"}
                        placeholder={"Desde"}
                        borderColor={COLORS.black_text_color3}
                        textColor={COLORS.black_text_color1}
                        active={true}
                        type={'date'}
                    />
                    <p className={globalStyle.error}>
                        <Paint color={COLORS.red_error2}>{UserForm.errors.fechaDesde && UserForm.errors.fechaDesde}</Paint>
                    </p>
                </MarginInput>

                <MarginInput width="30%">
                    <label>Hasta:</label>
                    <EditTextForm
                        value={UserForm.values.fechaHasta?.split('T')[0]}
                        onChange={UserForm.handleChange}
                        backgroundcolor={COLORS.white_color2(1)}
                        name={"fechaHasta"}
                        placeholder={"Hasta"}
                        borderColor={COLORS.black_text_color3}
                        textColor={COLORS.black_text_color1}
                        active={UserForm.values.temporal === 'true'}
                        type={'date'}
                    />
                    <p className={globalStyle.error}>
                        <Paint color={COLORS.red_error2}>{UserForm.errors.fechaHasta && UserForm.errors.fechaHasta}</Paint>
                    </p>
                </MarginInput>
            </GroupRow>

            <div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: listUsuarioProyectoRol.length < 1 ? 0 : 10 }}>
                    <MarginInput width="40%">
                        <OutLineButton color={!!detailUpdate ? COLORS.white_color1 : COLORS.blue_active} backgroundColor={!!detailUpdate ? COLORS.blue_main(1) : COLORS.transparent_color1} text={!!detailUpdate ? "Modificar Rol" : "Añadir Rol"} method={UserForm.handleSubmit} />
                        {listUsuarioProyectoRol.length < 1 &&
                            <p style={{ fontSize: "1.4em", textAlign: 'center' }} className={globalStyle.error}>
                                <Paint color={COLORS.red_error2}>Debe tener al menos un rol</Paint>
                            </p>
                        }
                    </MarginInput>
                </div>
                <DetailTable
                    props={{ rowKey: (record: any) => record.rolNombre }}
                    dataSource={listUsuarioProyectoRol}
                    columns={columns}
                    loading={false}
                    paginationActive={!detailUpdate}
                />
            </div>

        </div>
    );
}

export default StepFour;
